import { useRef, useState, type FormEvent } from "react";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import { RowsSkeleton } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts, type Label } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Field, FormActions, Notice, OptionCards, PageHead, TextField } from "../ui/kit";
import { OnceSecret, PageState, PasswordField } from "../ui/pieces";
import { scrollPageTop } from "../ui/scroll";
import { dateIn, joinList, moduleName, modulesByRoleKey, noInboxKey, roleKey, userHref } from "./organizations";
import { Check } from "./PersonActions";
import styles from "./orgs.module.css";

type Options = {
  name: string;
  code: string;
  timezone: string;
  firstOwner: boolean;
  modules: string[];
  moduleNames: Record<string, string>;
  /** Roles que entran a todos los módulos por su rol (Identity); null si no respondió: se eligen uno por uno. */
  allModulesRoles?: string[] | null;
  inboxes: { inboxId: number; name: string }[];
};

/** Identity dice si el rol ya entra a todos los módulos encendidos: entonces no se eligen uno por uno. */
const modulesByRole = (options: Options, role: string) => options.allModulesRoles?.includes(role) === true;

/**
 * Los módulos encendidos de la empresa a los que entra por su rol: «Todos: CRM, Empleados IA, ERP»; con uno solo, su
 * nombre («Todos» no va con uno). La ayuda debajo dice por qué.
 */
const allModulesText = (L: Label, options: Options) => {
  const names = joinList(options.modules.map((code) => moduleName(L, code, options.moduleNames)));
  return options.modules.length === 1 ? names : formatText(L("ADM_PPL_MODULES_ALL"), names);
};

type Mode = "create" | "invite";
export type Draft = {
  mode: Mode;
  firstName: string;
  lastName: string;
  email: string;
  role: "ORG_ADMIN" | "ORG_MEMBER";
  passwordMode: "auto" | "manual";
  password: string;
  confirm: string;
  forceChange: boolean;
  modules: string[];
  inboxId: string;
  inboxRole: "AGENT" | "SUPERVISOR";
  loginName: string;
};
type FieldName = "firstName" | "email" | "password" | "confirm" | "loginName";
type Reply = {
  saved?: boolean;
  field?: FieldName | null;
  messageKey?: string;
  role?: string;
  userId?: number;
  temporaryPassword?: string | null;
  inviteLink?: string;
  expiresAt?: string | null;
  pending?: string[];
  /** Lo que decidió el servidor al crear: su rol entra a todos, o los módulos concedidos uno por uno. */
  modulesByRole?: boolean;
  modules?: string[];
};

export const emptyDraft: Draft = {
  mode: "create",
  firstName: "",
  lastName: "",
  email: "",
  role: "ORG_MEMBER",
  passwordMode: "auto",
  password: "",
  confirm: "",
  forceChange: true,
  modules: [],
  inboxId: "",
  inboxRole: "AGENT",
  loginName: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Errores del formulario antes de enviarlo (el servidor vuelve a validar todo). */
export function validateDraft(draft: Draft, L: Label): Partial<Record<FieldName, string>> {
  const found: Partial<Record<FieldName, string>> = {};
  const creating = draft.mode === "create";
  const email = draft.email.trim();
  if (!email || !emailPattern.test(email) || email.length > 200) found.email = L("ORGCORE_EMAIL_REQUIRED");
  if (creating && draft.loginName.trim().length > 50) found.loginName = L("ADD_ERR_DATOS");
  if (creating && !draft.firstName.trim()) found.firstName = L("ORGCORE_NAME_REQUIRED");
  if (creating && draft.passwordMode === "manual") {
    if (draft.password.length < 12 || draft.password.length > 200) found.password = L("ADD_ERR_WEAK");
    else if (draft.password !== draft.confirm) found.confirm = L("ADD_ERR_CONFIRM");
  }
  return found;
}

const fieldOrder: FieldName[] = ["email", "loginName", "firstName", "password", "confirm"];

/**
 * `emp-usuario-nuevo` · Personas › ← Agregar usuario (§5.4, D6): formulario único en dos columnas. «Cómo entra»: Crear
 * ahora o Invitar por correo (con invitación se ocultan Contraseña, Al entrar, Módulos y Bandeja, y un aviso lo dice).
 * La primera persona de la empresa entra como propietario (el rol es un dato). Propietario y Administrador entran a
 * todos los módulos por su rol: se dicen como dato, sin casillas que no decidirían nada (C38). La bandeja es opcional y
 * su ayuda dice qué pasa sin ella según el rol; su función se elige solo con una bandeja elegida. Al crear, la misma
 * pantalla pasa al resultado (`emp-usuario-creado`) sin cambiar de dirección; el secreto se ve una sola vez (C51).
 */
export function UserNewPage({ id }: { id: number }) {
  const { L } = useTexts();
  const options = useAiData<Options>(`/api/admin/organizations/${id}/users/new`);
  const [done, setDone] = useState<{ draft: Draft; reply: Reply } | null>(null);
  const [version, setVersion] = useState(0);
  useOpenOrg(options.data ? { id, name: options.data.name, code: options.data.code } : null);
  useTitle(
    L(done ? (done.draft.mode === "invite" ? "ADM_PPL_INVITED_TITLE" : "ADM_PPL_CREATED_TITLE") : "ORG_ADD_USER"),
  );
  const back = { href: `/Organizations/Usuarios?id=${id}`, label: L("ADM_ORG_PEOPLE") };
  if (options.state === "missing") return <OrgNotFound />;
  if (options.state === "failed" && !options.data)
    return (
      <div className={styles.formPage}>
        <PageState
          head={<PageHead title={L("ORG_ADD_USER")} back={back} />}
          title={options.error?.status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
          line={options.error?.status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
          action={
            <button type="button" className="tm-btn secondary" onClick={options.retry}>
              {L("ADM_RETRY")}
            </button>
          }
        />
      </div>
    );
  if (!options.data)
    return (
      <div className={styles.formPage}>
        <PageHead title={L("ORG_ADD_USER")} back={back} />
        <RowsSkeleton rows={4} table />
      </div>
    );
  if (done)
    return (
      <UserDone
        orgId={id}
        options={options.data}
        done={done}
        onAnother={() => {
          setDone(null);
          setVersion((value) => value + 1);
          void options.reload();
          scrollPageTop();
        }}
      />
    );
  return (
    <UserForm
      key={version}
      id={id}
      options={options.data}
      onDone={(draft, reply) => {
        setDone({ draft, reply });
        scrollPageTop();
      }}
    />
  );
}

function UserForm({
  id,
  options,
  onDone,
}: {
  id: number;
  options: Options;
  onDone: (draft: Draft, reply: Reply) => void;
}) {
  const { L } = useTexts();
  const [draft, setDraft] = useState(emptyDraft);
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const creating = draft.mode === "create";
  // El rol con el que entra: la primera persona siempre es propietario (lo decide el servidor al crear).
  const role = options.firstOwner ? "ORG_OWNER" : draft.role;
  const allModules = modulesByRole(options, role);
  const set = (change: Partial<Draft>) => setDraft((current) => ({ ...current, ...change }));
  const clear = (field: FieldName) => setErrors((current) => ({ ...current, [field]: undefined }));
  const back = `/Organizations/Usuarios?id=${id}`;

  const focusField = (field: FieldName) =>
    requestAnimationFrame(() =>
      form.current?.querySelector<HTMLElement>(`[data-field="${field}"] input, #user-${field}`)?.focus(),
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found = validateDraft(draft, L);
    setErrors(found);
    setError(null);
    const first = fieldOrder.find((field) => found[field]);
    if (first) return focusField(first);
    setBusy(true);
    try {
      const reply = await postJson<Reply>(`/api/admin/organizations/${id}/users`, {
        mode: creating ? "create_now" : "invite",
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        email: draft.email.trim(),
        role: draft.role,
        passwordMode: draft.passwordMode,
        password: creating && draft.passwordMode === "manual" ? draft.password : null,
        forceChange: draft.forceChange,
        modules: creating && !allModules ? draft.modules : [],
        inboxId: creating && draft.inboxId ? Number(draft.inboxId) : null,
        inboxRole: draft.inboxRole,
        loginName: creating ? draft.loginName.trim() || null : null,
      });
      if (reply.status === 200 && reply.body?.saved) {
        // La contraseña escrita no pasa al resultado ni queda en memoria del formulario.
        onDone({ ...draft, password: "", confirm: "" }, reply.body);
        return;
      }
      const field = reply.body?.field ?? null;
      const message = L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC");
      if (field) {
        setErrors({ [field]: message });
        focusField(field);
      } else setError(message);
    } catch {
      setError(L("ADMIN_ERR_GENERIC"));
    } finally {
      setBusy(false);
    }
  };

  const pills = <T extends string>(
    labelledBy: string,
    value: T,
    values: { value: T; label: string }[],
    onChange: (value: T) => void,
  ) => (
    <div className={styles.choice} role="radiogroup" aria-labelledby={labelledBy}>
      {values.map((item) => (
        <button
          key={item.value}
          type="button"
          role="radio"
          className="tm-pill"
          aria-checked={value === item.value ? "true" : "false"}
          aria-pressed={value === item.value ? "true" : "false"}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
  const actions = (
    <>
      <a className="tm-btn secondary" href={back}>
        {L("ACTION_CANCEL")}
      </a>
      <button type="submit" form="user-new" className="tm-btn primary" disabled={busy} aria-busy={busy || undefined}>
        {L(creating ? "ADM_PPL_CREATE_USER" : "ADM_PPL_SEND_INVITE")}
      </button>
    </>
  );
  return (
    <div className={styles.formPage}>
      <PageHead title={L("ORG_ADD_USER")} back={{ href: back, label: L("ADM_ORG_PEOPLE") }}>
        <span className={ui.desktopOnly}>{actions}</span>
      </PageHead>
      <form
        id="user-new"
        ref={form}
        className={`tm-form ${ui.formGrid}`}
        noValidate
        onSubmit={(event) => void submit(event)}
      >
        {error ? (
          <div className={ui.formSummary}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}
        <div className={`${ui.full} ${styles.field}`} data-part="como-entra">
          <span className={styles.fieldTitle} id="user-how">
            {L("ADM_PPL_HOW")}
          </span>
          <OptionCards
            labelledBy="user-how"
            name="mode"
            value={draft.mode}
            onChange={(mode) => set({ mode: mode as Mode })}
            options={[
              { value: "create", title: L("ADM_PPL_HOW_CREATE"), line: L("ADM_PPL_HOW_CREATE_LINE") },
              { value: "invite", title: L("ADM_PPL_HOW_INVITE"), line: L("ADM_PPL_HOW_INVITE_LINE") },
            ]}
          />
        </div>
        <TextField
          id="user-email"
          field="email"
          label={L("ADM_PPL_EMAIL")}
          type="email"
          value={draft.email}
          error={errors.email}
          maxLength={200}
          placeholder={L("ADM_PPL_EMAIL_EXAMPLE")}
          onChange={(email) => (set({ email }), emailPattern.test(email.trim()) && clear("email"))}
        />
        <TextField
          id="user-login"
          field="loginName"
          label={L("ADM_PPL_LOGIN")}
          optional={L("CFG1_R1_OPTIONAL")}
          help={L("ADM_PPL_LOGIN_HELP")}
          value={draft.loginName}
          error={errors.loginName}
          maxLength={50}
          placeholder={L("ADM_PPL_LOGIN_EXAMPLE")}
          onChange={(loginName) => (set({ loginName }), clear("loginName"))}
        />
        <TextField
          id="user-first"
          field="firstName"
          label={L("REG_FIRST")}
          value={draft.firstName}
          error={errors.firstName}
          maxLength={100}
          placeholder={L("ADM_PPL_FIRST_EXAMPLE")}
          onChange={(firstName) => (set({ firstName }), firstName.trim() && clear("firstName"))}
        />
        <TextField
          id="user-last"
          label={L("REG_LAST")}
          optional={L("CFG1_R1_OPTIONAL")}
          value={draft.lastName}
          maxLength={100}
          placeholder={L("ADM_PPL_LAST_EXAMPLE")}
          onChange={(lastName) => set({ lastName })}
        />
        <div className={styles.field} data-part="rol">
          <span className={styles.fieldTitle} id="user-role">
            {L("ADM_PPL_ROLE")}
          </span>
          {options.firstOwner ? (
            <strong>{L("ADM_PPL_ROLE_OWNER")}</strong>
          ) : (
            pills(
              "user-role",
              draft.role,
              [
                { value: "ORG_ADMIN", label: L(roleKey("ORG_ADMIN")) },
                { value: "ORG_MEMBER", label: L(roleKey("ORG_MEMBER")) },
              ],
              (role) => set({ role }),
            )
          )}
          <small className={styles.help}>
            {/* Con personas en la empresa, lo que distingue a los dos roles (decisión del dueño, 2026-10-31). */}
            {L(options.firstOwner ? "ADM_PPL_FIRST_OWNER_FIXED" : "ADM_PPL_ROLE_HELP")}
          </small>
        </div>
        {creating ? (
          <>
            <div className={styles.field} data-part="contrasena">
              <span className={styles.fieldTitle} id="user-password-mode">
                {L("ADD_PWD_MODE_LABEL")}
              </span>
              {pills(
                "user-password-mode",
                draft.passwordMode,
                [
                  { value: "auto", label: L("ADM_PPL_PWD_AUTO") },
                  { value: "manual", label: L("ADM_PPL_PWD_MANUAL") },
                ],
                (passwordMode) => {
                  set({ passwordMode, password: "", confirm: "" });
                  clear("password");
                  clear("confirm");
                },
              )}
            </div>
            {draft.passwordMode === "manual" ? (
              <>
                <div data-field="password">
                  <PasswordField
                    id="user-password"
                    label={L("ADD_MANUAL_PWD")}
                    value={draft.password}
                    autoComplete="new-password"
                    error={errors.password}
                    help={L("ADM_PPL_PASSWORD_HELP")}
                    onChange={(password) => (set({ password }), password.length >= 12 && clear("password"))}
                  />
                </div>
                <div data-field="confirm">
                  <PasswordField
                    id="user-confirm"
                    label={L("ADD_MANUAL_PWD_CONFIRM")}
                    value={draft.confirm}
                    autoComplete="new-password"
                    error={errors.confirm}
                    onChange={(confirm) => (set({ confirm }), confirm === draft.password && clear("confirm"))}
                  />
                </div>
              </>
            ) : null}
            <div className={styles.field} data-part="al-entrar">
              <span className={styles.fieldTitle} id="user-first-login">
                {L("ADM_PPL_ON_LOGIN")}
              </span>
              <div role="group" aria-labelledby="user-first-login" className={ui.checks}>
                <Check checked={draft.forceChange} onChange={(forceChange) => set({ forceChange })}>
                  {L("ADM_PPL_FORCE_CHANGE_SHORT")}
                </Check>
              </div>
            </div>
            <div className={styles.field} data-part="modulos">
              <span className={styles.fieldTitle} id="user-modules">
                {L("ADM_PPL_MODULES")}
              </span>
              {options.modules.length === 0 ? (
                <span className={ui.muted}>{L("ADM_PPL_NO_ORG_MODULES")}</span>
              ) : allModules ? (
                <strong>{allModulesText(L, options)}</strong>
              ) : (
                <div role="group" aria-labelledby="user-modules" className={ui.checks}>
                  {options.modules.map((code) => {
                    const on = draft.modules.includes(code);
                    return (
                      <Check
                        key={code}
                        checked={on}
                        onChange={(value) =>
                          set({
                            modules: value ? [...draft.modules, code] : draft.modules.filter((item) => item !== code),
                          })
                        }
                      >
                        {moduleName(L, code, options.moduleNames)}
                      </Check>
                    );
                  })}
                </div>
              )}
              <small className={styles.help}>{L(allModules ? modulesByRoleKey(role) : "ADM_PPL_MODULES_HELP")}</small>
            </div>
            <Field
              id="user-inbox"
              label={L("ADM_PPL_INBOX")}
              optional={L("CFG1_R1_OPTIONAL")}
              help={draft.inboxId ? undefined : L(noInboxKey(role))}
            >
              <ChoiceMenu
                id="user-inbox"
                label={L("ADM_PPL_INBOX")}
                describedBy={draft.inboxId ? undefined : "user-inbox-help"}
                value={draft.inboxId}
                placeholder={L("ADM_PPL_CHOOSE_INBOX")}
                onChange={(inboxId) => set({ inboxId })}
                options={[
                  ...(draft.inboxId ? [{ value: "", label: L("ADM_PPL_NO_INBOX") }] : []),
                  ...options.inboxes.map((inbox) => ({ value: String(inbox.inboxId), label: inbox.name })),
                ]}
                searchLabel={L("CFG1_SEARCH")}
                clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
                searchIcon={<ChatIcon name="search" />}
                clearIcon={<ChatIcon name="x" />}
                chevron={<ChatIcon name="chevron-down" />}
                check={<ChatIcon name="check" />}
              />
            </Field>
            {/* La función solo decide algo con una bandeja elegida (un control sin efecto no se muestra, C38). */}
            {draft.inboxId ? (
              <div className={styles.field} data-part="funcion">
                <span className={styles.fieldTitle} id="user-inbox-role">
                  {L("ADM_PPL_INBOX_FUNCTION")}
                </span>
                {pills(
                  "user-inbox-role",
                  draft.inboxRole,
                  [
                    { value: "AGENT", label: L("ROLE_AGENT") },
                    { value: "SUPERVISOR", label: L("ROLE_SUPERVISOR") },
                  ],
                  (inboxRole) => set({ inboxRole }),
                )}
              </div>
            ) : null}
          </>
        ) : (
          <div className={ui.full}>
            <Notice tone="info">{L("ADM_PPL_INVITE_NOTICE")}</Notice>
          </div>
        )}
        <div className={`${ui.mobileOnly} ${ui.full}`}>
          <FormActions>{actions}</FormActions>
        </div>
      </form>
    </div>
  );
}

/**
 * `emp-usuario-creado` · el resultado en la misma pantalla: el aviso, la contraseña temporal o el enlace de un solo uso
 * (C51: nunca en la dirección ni en el navegador; al recargar no vuelve), los datos y «Ver usuario», «Agregar otro» y
 * «Volver a Personas». Lo que no se pudo aplicar después del alta (módulos, bandeja) se dice con su salida.
 */
function UserDone({
  orgId,
  options,
  done,
  onAnother,
}: {
  orgId: number;
  options: Options;
  done: { draft: Draft; reply: Reply };
  onAnother: () => void;
}) {
  const { L, locale } = useTexts();
  const { draft, reply } = done;
  const invite = draft.mode === "invite";
  const name = `${draft.firstName} ${draft.lastName}`.trim() || draft.email;
  const role = reply.role ?? draft.role;
  // Lo que decidió el servidor al crear manda sobre lo que se vio al abrir (la regla pudo no llegar entonces).
  const allModules = reply.modulesByRole ?? modulesByRole(options, role);
  const granted = reply.modules ?? draft.modules;
  const inbox = options.inboxes.find((item) => String(item.inboxId) === draft.inboxId);
  const people = `/Organizations/Usuarios?id=${orgId}`;
  const facts = [
    { label: L("ADM_PPL_EMAIL"), value: draft.email },
    { label: L("ADM_PPL_ROLE"), value: L(roleKey(role)) },
    ...(invite
      ? []
      : [
          {
            label: L("ADM_PPL_MODULES"),
            // Por su rol entra a todos los encendidos (no se concedió ninguno uno por uno).
            value: allModules
              ? options.modules.length
                ? allModulesText(L, options)
                : "—"
              : granted.length
                ? joinList(granted.map((code) => moduleName(L, code, options.moduleNames)))
                : "—",
          },
          {
            label: L("ADM_PPL_INBOX"),
            value: inbox
              ? `${inbox.name} · ${L(draft.inboxRole === "SUPERVISOR" ? "ROLE_SUPERVISOR" : "ROLE_AGENT")}`
              : "—",
          },
          {
            label: L("ADD_PWD_MODE_LABEL"),
            value: [
              L(reply.temporaryPassword ? "ADM_PPL_PWD_GENERATED" : "ADM_PPL_PWD_WRITTEN"),
              draft.forceChange ? L("ADM_PPL_PWD_MUST_CHANGE") : null,
            ]
              .filter(Boolean)
              .join(" · "),
          },
        ]),
  ];
  const zone = options.timezone;
  return (
    <div>
      <PageHead
        title={L(invite ? "ADM_PPL_INVITED_TITLE" : "ADM_PPL_CREATED_TITLE")}
        back={{ href: people, label: L("ADM_ORG_PEOPLE") }}
      />
      <div className={styles.done} data-part="resultado">
        <Notice tone="done">
          {invite
            ? reply.expiresAt
              ? formatText(L("ADM_PPL_INVITED_LINE"), draft.email, dateIn(locale, reply.expiresAt, zone))
              : formatText(L("ADM_PPL_INVITED_LINE_NODATE"), draft.email)
            : formatText(L("ADM_PPL_CREATED_LINE"), name, options.name)}
        </Notice>
        {invite && reply.inviteLink ? (
          <OnceSecret
            label={L("ADM_PPL_INVITE_LINK")}
            value={reply.inviteLink}
            help={L("ADM_PPL_INVITE_LINK_HELP")}
            copiedMessage={L("ADM_COPIED")}
          />
        ) : null}
        {!invite && reply.temporaryPassword ? (
          <OnceSecret
            label={L("ADM_SAMPLE_SECRET_LABEL")}
            value={reply.temporaryPassword}
            help={[L("ADM_PPL_SECRET_ONCE"), draft.forceChange ? L("ADM_PPL_MUST_CHANGE") : null]
              .filter(Boolean)
              .join(" ")}
            copiedMessage={L("ADM_SAMPLE_SECRET_COPIED")}
          />
        ) : null}
        <dl className={ui.facts} data-part="datos">
          {facts.map((item) => (
            <div key={item.label}>
              <dt>{item.label}</dt>
              <dd>{item.value}</dd>
            </div>
          ))}
        </dl>
        {(reply.pending ?? []).map((item) => (
          <Notice
            key={item}
            tone="error"
            action={
              reply.userId ? (
                <a
                  className="tm-link"
                  href={userHref(orgId, reply.userId, "active", item === "inbox" ? "bandejas" : "modulos")}
                >
                  {L("ADM_PPL_FIX_IN_PROFILE")}
                </a>
              ) : undefined
            }
          >
            {L(item === "inbox" ? "ADM_PPL_PENDING_INBOX" : "ADM_PPL_PENDING_MODULES")}
          </Notice>
        ))}
        <div className={styles.doneActions}>
          {invite ? (
            <a className="tm-btn primary" href={`/Organizations/Invitaciones?id=${orgId}`}>
              {L("ADM_PPL_SEE_INVITES")}
            </a>
          ) : reply.userId ? (
            <a className="tm-btn primary" href={userHref(orgId, reply.userId)}>
              {L("ADM_PPL_SEE_USER")}
            </a>
          ) : null}
          <button type="button" className="tm-btn secondary" onClick={onAnother}>
            {L("ADM_PPL_ADD_ANOTHER")}
          </button>
          <a className="tm-btn secondary" href={people}>
            {L("ADM_PPL_BACK_TO_PEOPLE")}
          </a>
        </div>
      </div>
    </div>
  );
}
